<template>
  <div>
    <PageHeader title="賞与設定" class="main-form">
      <template #title-header-content>
        <div class="flex-1 flex justify-end items-center">
          <!-- 賞与画面・管理者のみ閲覧、編集可能 -->
          <span class="text-white text-xs">※管理者・賞与画面権限者のみ閲覧可能</span>

          <div class="mx-auto">
            <div v-if="!isBonusDec">
              <PrimaryButton text="賞与 確定" @click="bonusDec()" :buttonContents="'要'" class="bg-yellow-600" :disabled="!admin" v-tooltip="'管理者権限のみ確定可能'">
                <Icon v-if="!loading" slot="before" iconName="CheckCircle" iconType="outline" :strokeWidth="2" class="h-5 w-5 mr-2" />
                <LoadingIcon v-else slot="before" class="h-4 w-4 my-auto mr-2 text-white" />
              </PrimaryButton>
            </div>
            <div v-else-if="!timeOver" class="flex">
              <div class="text-pink-50 self-center font-bold text-xs mr-3">※賞与確定済みです。編集する場合は「賞与確定 解除」を押してください➡</div>
              <PrimaryButton :text="'賞与確定 解除'" @click="bonusCancel()" :buttonContents="'要'" class="bg-red-600" :disabled="!admin" v-tooltip="'管理者権限のみ解除可能'"></PrimaryButton>
            </div>
          </div>
          <!-- 年度検索 -->
          <div class="mr-2 self-center flex">
            <input
              type="number"
              name="filterYear"
              min="2024"
              max="2999"
              placeholder="年度 検索"
              class="py-2 border border-gray-300 rounded-md leading-5 focus:bg-white focus:text-gray-900 font-bold w-20 text-center"
              v-model="filter.year"
              @change="getData()"
            />
            <span class="pl-1 pr-2 font-bold text-white text-sm self-end">年度</span>
          </div>

          <!-- 期 -->
          <div class="mr-2 font-bold text-white self-center flex">
            <span class="text-4xl">{{ term }}</span>
            <span class="pl-1 pr-2 text-sm self-end">期</span>
          </div>

          <!-- 切替 -->
          <div class="bg-white rounded mr-5">
            <div class="flex items-center py-1 rounded mx-5 ">
              <div v-for="tab in periodList"
                :key="tab.id"
                class="w-28 text-center mx-1 cursor-pointer"
                :class="[period == tab.id ? 'bg-light-blue-700' : 'hover:bg-gray-400', 'bg-gray-300 rounded-md py-1 px-3 text-sm font-medium text-white']"
                @click="periodChanged(tab)"
              >
                {{ tab.title }}
              </div>
            </div>
          </div>

          <!-- ハンコ表 -->
          <PrimaryButton text="ハンコ表" @click="stampTable()" :buttonContents="'要'" class="mr-5 bg-pink-600">
            <Icon v-if="!loading" slot="before" iconName="ClipboardCheck" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
            <LoadingIcon v-else slot="before" class="h-4 w-4 my-auto ml-2 text-white" />
          </PrimaryButton>

          <!-- 出力 -->
          <PrimaryButton text="出力" @click="download()" :buttonContents="'要'" class="mr-1 bg-blue-600">
            <Icon v-if="!loading" slot="before" iconName="Download" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
            <LoadingIcon v-else slot="before" class="h-4 w-4 my-auto ml-2 text-white" />
          </PrimaryButton>

          <!-- リロードアイコン -->
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': loading }"
            class="text-white hover:text-gray-300 active:text-gray-400 ml-4 cursor-pointer" @click.native="refresh"
          />
        </div>
      </template>
      <template #page-header-content>
        <div v-if="!notDisp" class="flex">
          <!-- 加点 -->
          <div class="text-white font-bold">
            総額
            <input
              type="number"
              placeholder="入力必須"
              class="border border-gray-300 rounded focus:ring-white text-center font-bold text-black w-28 text-lg py-1"
              :class="bonus?'':'bg-red-400'"
              v-model="bonus"
              :disabled="isBonusDec||!admin"
              @change="changeBonus()"
            />
            万円
          </div>

          <!-- グループ切替 -->
          <div class="bg-white rounded w-fit ml-auto mr-3">
            <div class="flex items-center py-1 rounded mx-5">
              <div v-for="tab in groupList"
                :key="tab.id"
                class="w-20 text-center mx-1 cursor-pointer"
                :class="[group == tab.id ? 'bg-light-blue-700' : 'hover:bg-gray-400', 'bg-gray-300 rounded-md py-1 px-3 text-sm font-medium text-white']"
                @click="groupChanged(tab)"
              >
                {{ tab.title }}
              </div>
            </div>
          </div>

          <!-- 上司検索 -->
          <div class="mr-2">
            <input
              type="search"
              name="filterBossr"
              placeholder="上司 検索"
              class="py-2 border border-gray-300 rounded-md leading-5 focus:bg-white focus:text-gray-900 w-36"
              v-model="filter.boss"
            />
          </div>

          <!-- 未入力検索 -->
          <div class="mr-2">
            <select
              v-model="filter.input"
              class="border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 text-sm py-2 text-gray-700"
            >
              <option
                v-for="row in inputList" :key='row.id'
                :value="row.id"
              >
                {{ row.name }}
              </option>
            </select>
          </div>

          <!-- 検索ボタン -->
          <PrimaryButton
            text="検索"
            class="button-contents mr-2"
            @click="getData()"
          >
          </PrimaryButton>
        </div>
      </template>
    </PageHeader>

    <main class="absolute top-32 bottom-4 leading-8 w-full main-form mx-auto main-height">
      <div class="mx-auto px-2 sm:px-4 lg:px-8 h-full w-full">
        <div v-if="errData.length" class="p-2 bg-red-100">
          <div class="text-red-800 font-bold">不整合データ</div>
          <div class="p-2 whitespace-pre-line">{{ errData.join('\r\n') }}</div>
        </div>
        <div v-if="!notDisp" class="border-blue-700 border">
          <div @click="showInfo=!showInfo" class="text-white font-bold px-3 py-1 cursor-pointer" :class="loading?'animate-pulse bg-green-500':'bg-blue-700'">
            <span v-if="showInfo" class="pr-5">▼</span><span v-else class="animate-pulse pr-5">▶</span>
            配分表、単価 など
            <span v-if="loading" class="inline-block text-center w-4/5">Loading...</span>
          </div>
          <div v-if="showInfo" class="bg-white p-2 flex">
            <!-- 配分表 -->
            <div class="border border-gray-300 rounded mx-2 w-fit p-2">
              <div class="flex font-bold">
                <p class="font-bold text-blue-700">点数配分表</p>
                <p class="text-red-700 w-fit mx-auto">※賞与対象外はこの表に含まれません</p>
              </div>

              <table>
                <thead>
                  <tr>
                    <th></th>
                    <th :colspan="2" class="text-center">SS</th>
                    <th :colspan="2" class="text-center">S</th>
                    <th :colspan="2" class="text-center">A</th>
                    <th :colspan="2" class="text-center">B</th>
                    <th :colspan="2" class="text-center">C</th>
                    <th :colspan="2" class="text-center">D</th>
                    <th :colspan="2" class="text-center">E</th>
                    <th class="px-2"></th>
                  </tr>
                  <tr>
                    <th>グループ</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="text-right text-xs pr-2">点数</th>
                    <th class="text-left text-xs">(人数)</th>
                    <th class="px-2">合計点</th>
                  </tr>
                </thead>
                <tbody v-for="(row, i) in pointList" :key="'p'+i" class="text-sm">
                  <!-- 合計行以外 -->
                  <tr v-if="i!=6" :class="group==row.groupNum||group==2&&row.groupNum==1?'bg-green-100':''" class="hover-color">
                    <td class="text-center px-4 font-bold">{{ row.groupNum }}</td>
                    <td class="text-right pl-4 pr-1">{{ row.SS }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numSS }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.S }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numS }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.A }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numA }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.B }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numB }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.C }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numC }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.D }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numD }}<span class="text-gray-600">）</span></td>
                    <td class="text-right pl-4 pr-1">{{ row.E }}</td>
                    <td class="text-right pr-4 pl-1"><span class="text-gray-600">（</span>{{ row.numE }}<span class="text-gray-600">）</span></td>
                    <td class="px-2 text-right">{{ row.total?row.total.toLocaleString():null }}</td>
                  </tr>
                  <tr v-else class="hover-color border-t-2 border-gray-400 font-bold">
                    <td class="text-center px-4 pt-3">{{ row.groupNum }}</td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numSS }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numS }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numA }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numB }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numC }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numD }} <span class="text-gray-700 font-normal">人</span></td>
                    <td class="text-center pt-3" :colspan="2">{{ row.numE }} <span class="text-gray-700 font-normal">人</span></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- 平均単価 -->
            <div class="mx-2">
              <div class="p-2 border border-gray-300 rounded">
                <p class="font-bold text-blue-700">平均単価</p>
                <div class="flex">
                  <div>
                    <div class="text-xs font-bold text-green-600">総額</div>
                    <div>{{ bonus2.toLocaleString() }}</div>
                  </div>
                  <div class="font-bold mt-auto mb-1 mx-3">-</div>
                  <div>
                    <div class="text-xs font-bold text-green-600">手当</div>
                    <div>{{ benefit.toLocaleString() }}</div>
                  </div>
                  <div class="font-bold mt-auto mb-1 mx-3">＝</div>
                  <div>
                    <div class="text-xs font-bold text-green-600">賞与配当金額</div>
                    <div>{{ (bonus2 - benefit).toLocaleString() }}</div>
                  </div>
                </div>
                <div class="flex mt-3">
                  <div>
                    <div class="text-xs font-bold text-green-600">賞与配当金額</div>
                    <div>{{ (bonus2 - benefit).toLocaleString() }}</div>
                  </div>
                  <div class="font-bold mt-auto mb-1 mx-3">÷</div>
                  <div>
                    <div class="text-xs font-bold text-green-600">配分表合計点</div>
                    <div>{{ totalPoints }}</div>
                  </div>
                  <div class="font-bold mt-auto mb-1 mx-3">＝</div>
                  <div class="bg-blue-50 px-2">
                    <div class="text-xs font-bold">単価</div>
                    <div>{{ uniPrice.toLocaleString() }}</div>
                  </div>
                </div>
                <div class="font-bold mt-3 p-2 bg-blue-50 w-fit">{{ uniPrice.toLocaleString() }} 円</div>
                <div class="font-bold mt-3 text-red-700 text-sm">※総額、手当、始末書、係数、評価が変わると<br> 平均単価も変わります。そのため、下の表を確定後に<br> 平均単価を参考にしてください。</div>
              </div>
            </div>

            <!-- 単価 -->
            <div class="mx-2">
              <div class="p-2 border border-gray-300 rounded">
                <!-- <p class="font-bold text-blue-700">グループごとの単価</p> -->
                <table>
                  <thead>
                    <tr class="text-blue-700">
                      <th>グループ</th>
                      <th class="">単価</th>
                    </tr>
                  </thead>
                  <tbody v-for="(row, i) in unitPriceList" :key="'p'+i" class="text-sm">
                    <tr class="hover-color">
                      <td class="text-center px-4 font-bold">{{ row.groupNum }}</td>
                      <td class="">
                        <input
                          type="number"
                          class="border border-gray-300 rounded focus:ring-white focus:bg-yellow-100 text-center font-bold text-black w-20 py-1"
                          v-model="row.unitPrice"
                          :disabled="isBonusDec||!admin"
                          @focus="inputFocus"
                          @change="changeGroupUnitPrice(row)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 残高 -->
            <div class="mx-2">
              <div class="p-2 border border-gray-300 rounded">
                <p class="font-bold text-blue-700">結果</p>
                <table>
                  <tr>
                    <th class="pr-3 text-left">総額</th>
                    <td class="text-right">{{ bonus2.toLocaleString() }}</td>
                  </tr>
                  <tr>
                    <th class="pr-3 text-left">支給</th>
                    <td class="text-right">{{ totalPayment.toLocaleString() }}</td>
                  </tr>
                  <tr>
                    <th class="pr-3 text-left">残高</th>
                    <td class="text-right" :class="balance<0?'bg-red-100':''">{{ balance.toLocaleString() }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div v-if="list.length" class="bg-white rounded-md border border-gray-200 overflow-auto" :class="showInfo?'table-height':'table-height2'">
          <table class="table overflow-auto text-sm">
            <thead class="th sticky top-0 z-20 bg-colu">
              <tr class="text-gray-500 text-xs border border-gray-200 bg-colu">
                <th v-for="(c, i) in columns" :key="'col'+i" class="whitespace-pre-line py-2 px-3" :class="c.class">{{ c.val }}</th>
              </tr>
            </thead>
            <tbody v-for="(row, i) in list" :key="i">
              <tr class="hover-color" :class="[setStyle(row, i), row.na?'bg-gray-200':row.err?'bg-red-200':'']">

                <!-- 評価対象外（賞与画面に不要） -->
                <td class="text-gray-700 min-w-fit text-center font-bold">
                  <input
                    type="checkbox"
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded p-2"
                    v-model="row.na"
                    :disabled="isBonusDec"
                    @change="changeNA(row, 'na')"
                  />
                </td>

                <!-- 賞与対象外（ボーナス無しで評価のみ行う） -->
                <td class="text-gray-700 min-w-fit text-center font-bold">
                  <input
                    type="checkbox"
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded p-2"
                    v-model="row.naBonus"
                    :disabled="isBonusDec"
                    @change="changeNA(row, 'naBonus')"
                  />
                </td>

                <!-- 順位 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-center font-bold" :class="setStyle2(row.wRank)">
                  {{ row.rankNum }}
                  <div v-if="row.wRank>1" class="font-bold text-yellow-800 text-sm mt-1">同位です</div>
                </td>

                <!-- 名前/部署 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-center border-r" :class="setStyle2(row.wRank)">
                  <div v-if="!group||group==2" class="text-center text-gray-700">{{ row.groupNum }} G</div>
                  <div class="font-bold">{{ row.userName }}</div>
                  <div class="text-gray-500 text-xs mt-1.5">{{ row.departmentName }}</div>
                </td>

                <!-- 上司 -->
                <td class="whitespace-nowrap text-xs text-gray-700 min-w-fit py-2 px-3 text-center border-r">
                  {{ row.boss }}
                  <!-- 評価シート確認 -->
                  <div class="mt-1 border-b border-blue-600 pt-1 px-1 cursor-pointer w-fit text-blue-600 mx-auto" @click="toEvaluationForm(row)">確認</div>
                </td>

                <!-- 点数 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-right border-r w-100px">
                  <div class="mb-2 text-blue-800 font-bold">{{ row.process }}</div>
                  <div class="text-pink-700 font-semibold">{{ row.policy }}</div>
                </td>

                <!-- 調整 -->
                <td class="py-2 px-3 w-28 border-r">
                  <input
                    v-if="!row.na"
                    type="number"
                    class="border border-gray-300 rounded focus:ring-white text-center w-28"
                    :class="row.addPoints?'bg-yellow-300 font-bold':'text-gray-500'"
                    v-model="row.addPoints"
                    :disabled="!admin||isBonusDec"
                    @focus="inputFocus"
                    @change="changeAddPoint(row, $event)"
                  />
                </td>

                <!-- 合計 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-right border-r w-100px font-bold">
                  {{ row.totalPoints }}
                </td>

                <!-- 評価 -->
                <td class="whitespace-nowrap text-sm text-gray-700 py-2 px-3 text-center border-r">
                  <select
                    v-if="!row.na"
                    class="border border-gray-300 rounded focus:ring-white text-center"
                    v-model="row.rankName"
                    :disabled="!admin||isBonusDec"
                    :class="row.rankName!=row.rankNameDefault?'bg-yellow-300 font-bold':'text-gray-500'"
                    @change="changeRank(row)"
                  >
                    <option v-for="(rank) in rankList" :value="rank" :key="rank+i" class="text-center">
                      {{ rank }}
                    </option>
                  </select>
                </td>

                <!-- 賞与 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-right border-r">
                  <div v-if="row.naBonus" class="text-center">×</div>
                  <span v-else-if="row.bonus||row.bonus==0">{{ row.bonus.toLocaleString() }}</span>
                </td>

                <!-- 禁煙手当 -->
                <td class="whitespace-nowrap text-sm text-gray-700 p-1 text-center border-r">
                  <div v-if="row.smokingDefault==null&&!row.na&&!row.naBonus" class="text-red-600 text-xs">本人未入力</div>
                  <select
                    v-if="!row.na&&!row.naBonus"
                    class="border border-gray-300 rounded focus:ring-white text-center text-sm"
                    v-model="row.smoking"
                    :class="row.smoking!=row.smokingDefault?'bg-yellow-300':'text-gray-600'"
                    :disabled="isBonusDec"
                    @change="changeSmoking(row)"
                  >
                    <option v-for="(smoke) in smokingList" :value="smoke.key" :key="String(smoke.key)+i" class="text-center">
                      {{ smoke.val }}
                    </option>
                  </select>
                  <div v-else-if="row.naBonus" class="text-center">×</div>
                </td>

                <!-- 安全運転 -->
                <td class="whitespace-nowrap text-sm text-gray-700 p-1 text-center border-r">
                  <div v-if="row.drivePriceDefault==null&&!row.na&&!row.naBonus" class="text-red-600 text-xs">本人未入力</div>
                  <select
                    v-if="!row.na&&!row.naBonus"
                    class="border border-gray-300 rounded focus:ring-white text-center text-sm"
                    v-model="row.drivePrice"
                    :class="row.drivePrice!=row.drivePriceDefault?'bg-yellow-300':'text-gray-600'"
                    :disabled="isBonusDec"
                    @change="changeDrivePrice(row)"
                  >
                    <option v-for="(drive) in drivePriceList" :value="drive.key" :key="String(drive.key)+i+i" class="text-center">
                      {{ drive.val }}
                    </option>
                  </select>
                  <div v-if="row.selfDrivePrice&&!row.na&&!row.naBonus" class="text-blue-700 font-bold text-xs">※1万5千円自己申告</div>
                  <div v-else-if="row.naBonus" class="text-center">×</div>
                </td>

                <!-- 始末書 -->
                <td class="whitespace-nowrap text-sm text-gray-700 p-1 text-center border-r">
                  <div class="flex justify-center">
                    <div class="mx-0.5">
                      <p class="text-xs text-gray-700 font-bold">始末書</p>
                      <input
                        v-if="!row.na"
                        type="number"
                        class="border border-gray-300 rounded focus:ring-white text-center w-14 text-sm px-1"
                        :class="row.apologyDefault!=row.apology?'bg-yellow-300':'text-gray-600'"
                        v-model="row.apology"
                        :disabled="isBonusDec"
                        @focus="inputFocus"
                        @change="changeApology(row, 'apology', $event)"
                      />
                    </div>
                    <!-- 反省文 -->
                    <div class="mx-0.5">
                      <p class="text-xs text-gray-700 font-bold">反省文</p>
                      <input
                        v-if="!row.na"
                        type="number"
                        class="border border-gray-300 rounded focus:ring-white text-center w-14 text-sm px-1"
                        :class="row.reflectDefault!=row.reflect?'bg-yellow-300':'text-gray-600'"
                        v-model="row.reflect"
                        :disabled="isBonusDec"
                        @focus="inputFocus"
                        @change="changeApology(row, 'reflect', $event)"
                      />
                    </div>
                  </div>

                  <div class="flex text-blue-700 font-bold text-xs justify-center">
                    <div v-if="row.bonusDivideApology">※{{ row.bonusDivideApology }}分の1</div>
                    <div v-if="row.apologyDefault||row.reflectDefault" class="ml-3 px-1.5 border-b border-blue-700 cursor-pointer" @click="openApology(row)">確認</div>
                  </div>


                </td>

                <!-- 週休3日 -->
                <td class="p-1 text-center w-32 border-r whitespace-nowrap">
                  <div v-if="!row.na&&!row.naBonus" class="flex w-fit mx-auto">
                    <!-- 分母 -->
                    <input
                      type="number"
                      class="border border-gray-300 rounded focus:ring-white text-center w-10"
                      :class="row.weeklyHolidayDenominatorDefault!=row.weeklyHolidayDenominator?'bg-yellow-300 font-bold':'text-gray-500'"
                      v-model="row.weeklyHolidayDenominator"
                      :disabled="isBonusDec"
                      @focus="inputFocus"
                      @change="changeCoef('週休3日', row.weeklyHolidayNumerator, row, $event)"
                    />
                    <div class="self-center text-xs font-bold text-gray-700 mx-0.5">分の</div>
                    <!-- 分子 -->
                    <input
                      type="number"
                      class="border border-gray-300 rounded focus:ring-white text-center w-10"
                      :class="row.weeklyHolidayNumeratorDefault!=row.weeklyHolidayNumerator?'bg-yellow-300 font-bold':'text-gray-500'"
                      v-model="row.weeklyHolidayNumerator"
                      :disabled="isBonusDec"
                      @focus="inputFocus"
                      @change="changeCoef('週休3日', row.weeklyHolidayDenominator, row, $event)"
                    />
                  </div>
                  <div v-if="row.weeklyHolidayDenominatorDefault" class="text-blue-700 font-bold text-xs">※対象者</div>
                </td>

                <!-- 係数 -->
                <td class="p-1 text-center w-32 border-r whitespace-nowrap">
                  <div v-if="!row.na&&!row.naBonus" class="flex w-fit mx-auto">
                    <input
                      type="number"
                      class="border border-gray-300 rounded focus:ring-white text-center w-10"
                      :class="row.denominator?'bg-yellow-300 font-bold':'text-gray-500'"
                      v-model="row.denominator"
                      :disabled="isBonusDec"
                      @focus="inputFocus"
                      @change="changeCoef('係数', row.numerator, row, $event)"
                    />
                    <div class="self-center text-xs font-bold text-gray-700 mx-0.5">分の</div>
                    <input
                      type="number"
                      class="border border-gray-300 rounded focus:ring-white text-center w-10"
                      :class="row.numerator?'bg-yellow-300 font-bold':'text-gray-500'"
                      v-model="row.numerator"
                      :disabled="isBonusDec"
                      @focus="inputFocus"
                      @change="changeCoef('係数', row.denominator, row, $event)"
                    />
                  </div>
                  <div v-if="row.bossCount&&row.bossCount<6" class="text-blue-700 font-bold text-xs">上司面談{{ row.bossCount }}回のみ</div>
                  <div v-else-if="!row.bossCount&&!row.na" class="text-red-700 font-bold text-xs">上司面談0回</div>
                  <div v-if="row.fullTime&&Number(row.fullTime)<8" class="text-blue-700 font-bold text-xs">※時短{{ row.fullTime }}h</div>
                </td>

                <!-- 入社日 -->
                <td class="whitespace-nowrap text-sm text-gray-700 min-w-fit py-2 px-3 text-center border-r">
                  <div v-if="!row.na" class="text-xs">{{ row.startDate }}</div>
                  <div v-if="!row.na">{{ row.workingYear }}<span class="text-xs text-gray-600 pl-1">年</span></div>
                </td>

                <!-- 賞与総額 -->
                <td class="whitespace-nowrap font-bold text-base text-gray-700 min-w-fit py-2 px-3 text-right border-r" :class="row.naBonus?'bg-gray-200':''">
                  <div v-if="row.naBonus">0</div>
                  <span v-else-if="row.bonusTotal||row.bonusTotal==0">{{ row.bonusTotal.toLocaleString() }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="!loading && !notDisp && !list.length" class="w-fit mx-auto mt-10 text-gray-400">
          <EmptyMessage
            iconName="Document"
            message="該当するデータはありません。"
          />
        </div>
        <div v-else-if="notDisp" class="w-fit mx-auto mt-10 text-gray-400">
          <EmptyMessage
            iconName="Document"
            :message="'管理者権限をもっていない方は過去の期しか確認できません。\r\n8月1日から今年度の前期、2月1日から去年度の後期が確認できます。'"
          />
        </div>
      </div>
    </main>
    <!-- 始末書集計 -->
    <EvaluationApologyModal
      v-model="evaluationApologyModalShow"
      :open="evaluationApologyModalShow"
      :userId="apologyModalData.userId"
      :userName="apologyModalData.userName"
      :bonusYear="apologyModalData.bonusYear"
      :bonusPeriod="apologyModalData.bonusPeriod"
      @closed="evaluationApologyModalShow=false">
    </EvaluationApologyModal>
    <!-- ハンコ表 -->
    <EvaluationStampModal
      v-model="openStampTableShow"
      :open="openStampTableShow"
      :bonusYear="filter.year"
      @closed="openStampTableShow=false">
    </EvaluationStampModal>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import EmptyMessage from '@components/EmptyMessage.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import LoadingIcon from '@assets/loading-circle.svg'
import EvaluationApologyModal from '../components/EvaluationApologyModal.vue'
import EvaluationStampModal from '../components/BonusStampModal.vue'
import * as logManager from '@managers/logManager'
import * as backend from '@libs/backend'
import * as utils from '@libs/utils'
import * as moment from 'moment'
import * as dialogs from '@libs/dialogs'
import PageModel from '../../main/pages/PageModel.vue'
import { FOUNDED } from '@/config'

const RANK_LIST = ['SS', 'S', 'A', 'B', 'C', 'D', 'E']

// 評価シート
const COL = [
  { val: '評価対象外', class: 'text-center' },
  { val: '賞与支給無', class: 'text-center' },
  { val: '順位', class: 'text-center' },
  { val: '名前\r\n部署', class: 'text-center' },
  { val: '上司\r\n評価シート', class: 'text-center' },
  { val: 'プロセス 点\r\n方針共有点', class: 'text-right' },
  { val: '調整点', class: 'text-center' },
  { val: '合計点', class: 'text-right' },
  { val: '評価', class: 'text-center' },
  { val: '賞与', class: 'text-right' },
  { val: '禁煙手当', class: 'text-center' },
  { val: '安全運転手当', class: 'text-center' },
  { val: '始末書・反省文', class: 'text-center' },
  { val: '週休3日', class: 'text-center' },
  { val: '係数（時短、休職）', class: 'text-center' },
  { val: '入社日\r\n年数', class: 'text-center' },
  { val: '賞与総額', class: 'text-right' },
]

export default {
  extends: PageModel,
  components: {
    Icon,
    PageHeader,
    EmptyMessage,
    PrimaryButton,
    LoadingIcon,
    EvaluationApologyModal,
    EvaluationStampModal
  },

  data() {
    return {
      loading: false,
      // 切替ボタン
      periodList: [
        { id: 1, title: '上期' },
        { id: 2, title: '下期' }
      ],
      groupList: [
        { id: 6, title: '6G' },
        { id: 5, title: '5G' },
        { id: 4, title: '4G' },
        { id: 3, title: '3G' },
        { id: 2, title: '2G・1G' },
        { id: null, title: '全て' },
      ],
      period: null,
      periodName: null,
      group: 6,
      // リスト
      bonusList: [],
      // カラム
      columns: utils.clone(COL),
      // ランク名リスト
      rankList: RANK_LIST,
      // 禁煙手当リスト
      smokingList: [{ key: 0, val: '0' }, { key: 50000, val: '50,000' }],
      // 安全運転手当リスト
      drivePriceList: [{ key: 0, val: '0' }, { key: 15000, val: '15,000' }, { key: 30000, val: '30,000' }],
      // 検索条件
      filter: {
        // 年度
        year: null,
        // 上司
        boss: null,
        // 入力
        input: null
      },
      // 候補
      inputList: [
        { id: null, name: '全て' },
        { id: 1, name: '手当 未入力' },
        { id: 2, name: '週休3日対象者' },
        { id: 3, name: '週休3日係数あり' },
        { id: 4, name: '上司面談6回未満' },
        { id: 5, name: '時短対象者' },
        { id: 6, name: '係数(時短、休職)あり' },
      ],
      // 対象の期の配分金の総額（万単位）
      bonus: null,
      // 対象の期の配分金の総額
      bonus2: 0,
      // 配分表
      pointList: [],
      // グループごとの単価
      unitPriceList: [],
      // 画面に表示するリスト
      list: [],
      // その他情報表示切替
      showInfo: true,
      // 単価
      uniPrice: 0,
      // 配分点合計
      totalPoints: 0,
      // 総支払い額
      totalPayment: 0,
      // 残高
      balance: 0,
      // 手当合計
      benefit: 0,
      // 管理者権限
      admin: false,
      // 賞与画面権限
      auth: false,
      // 閲覧不可期間
      notDisp: false,
      // 不正な値
      errData: [],
      // 賞与確定済み
      isBonusDec: false,
      // 始末書集計open
      evaluationApologyModalShow: false,
      // 始末書集計に渡すデータ
      apologyModalData: { userId: null, userName: null },
      // ハンコ表open
      openStampTableShow: false,
      // 期
      term: null
    }
  },

  async created() {
    let auth2 = this.$store.getters.auth
    if (auth2) {
      // 管理者権限の場合
      if (auth2.adminAuth) {
        this.auth = true
        this.admin = true
      // 賞与画面権限
      } else if (auth2.bonusAuth) {
        this.auth = true
      }
    }

    // 権限
    if (!this.auth) {
      alert('この画面は権限のある人しか見れません。\r\nメニューへ戻ります。')
      this.$router.push('/')
      return
    }

    // 年度
    this.filter.year = Number(moment().format('YYYY'))
    const mm = Number(moment().format('M'))

    // 基本的に前回の期を確認するので、
    // 1～3月までは去年度の後期
    if (mm <= 3) {
      this.filter.year--
      this.period = 2
    // 4～9月までは上期
    } else if (mm <= 9) {
      this.period = 1
    // 10～12月までは後期
    } else {
      this.period = 2
    }
    await this.getData()
  },

  computed: {
    /**
     * 解除期間内か
     */
    timeOver() {
      // 今日から7か月前
      const monthsAgo = moment().subtract(7, 'months').format('YYYY-MM-DD')
      const yy = Number(this.filter.year)
      // 上期なら対象は2月
      let mm = '-02'
      // 下期なら対象は8月
      if (this.period == 2) {
        mm = '-08'
      }
      // 対象日
      const tarM = yy + mm + '-01'

      // 対象日が本日より8か月前の場合は編集不可
      if (monthsAgo > tarM) {
        return true
        
      } else {
        return false
      }
    },
  },

  methods: {
    /**
     * リフレッシュアイコンイベント
     */
    async refresh() {
      await this.getData()
    },

    /**
     * データ取得
     */
    async getData() {
      this.term = Number(this.filter.year) - FOUNDED
      if (!this.filter.year) {
        await dialogs.showErrorDialog('年度未入力', '年度を入力してください。')
        return
      }
      if (!this.period) {
        await dialogs.showErrorDialog('期 未選択', '期を選択してください。')
        return
      }

      this.periodName = this.periodList.find((p) => {
        return p.id == this.period
      }).title

      // 管理者以外は過去分のみ閲覧可能
      if (!this.admin) {
        this.notDisp = false
        let y = Number(moment().format('YYYY'))
        let m = Number(moment().format('M'))
        let period = 1
        // 1月は後期がNG
        if (m == 1) {
          y--
          period = 2
        // 8月以降は後期がNG
        } else if (m >= 8) {
          period = 2
        }
        // 現在、前期の期間
        if (period == 1) {
          // 今年度、もしくは未来
          if (Number(this.filter.year) >= y) {
            this.notDisp = true
            return
          }
        // 現在が後期
        } else {
          // 今年度
          if (Number(this.filter.year) == y) {
            // 後期はNG
            if (this.period == 2) {
              this.notDisp = true
              return
            }
          } else if (Number(this.filter.year )> y) {
            this.notDisp = true
            return
          }
        }
      }

      this.loading = true
      // this.list = []
      // this.bonusList = []
      // this.pointList = []
      // this.unitPriceList = []
      // this.bonus = 0
      this.bonus2 = 0
      this.uniPrice = 0
      this.totalPoints = 0
      this.totalPayment = 0
      this.benefit = 0
      this.errData = []
      this.balance = 0
      this.isBonusDec = false

      // 検索開始日
      let start = ''
      // 検索終了日
      let end = ''

      if (this.period == 1) {
        start = this.filter.year + '-02-01'
        end = this.filter.year + '-07-31'
      } else {
        start = this.filter.year + '-08-01'
        end = (Number(this.filter.year) + 1) + '-01-31'
      }
      // データ取得
      const res = await backend.searchData('evaluation/getBonusList', { ...this.filter, period: this.period, start, end })
      if (res.data.data) {
        this.bonusList = res.data.data.list
        this.pointList = res.data.data.pointList
        this.unitPriceList = res.data.data.unitPriceList
        this.isBonusDec = res.data.data.isBonusDec

        if (res.data.data && res.data.data.bonus && res.data.data.bonus.totalPrice) {
          // 総額（万単位）
          this.bonus = Number(res.data.data.bonus.totalPrice) / 10000
          this.bonus2 = Number(res.data.data.bonus.totalPrice)
        }

        if (this.bonusList.length) {
          // 総支給額
          this.totalPayment = Number(this.bonusList[0].totalPayment || 0)
          // 手当合計
          this.benefit = Number(this.bonusList[0].benefit || 0)
        }

        // 配分点合計
        this.pointList.forEach((p) => {
          if (p.total) {
            this.totalPoints += p.total
          }
        })
        // 平均単価を求める
        // (総額ー手当（減額考慮）) ÷ 合計点
        const haitou = (this.bonus2 - this.benefit) / this.totalPoints
        this.uniPrice = Math.floor(haitou)
      } else {
        this.list = []
        this.bonusList = []
        this.unitPriceList = []
        this.pointList = []
        this.bonus = 0
      }

      // 残高
      this.balance = this.bonus2 - this.totalPayment

      if (this.balance < 0) {
        this.errData.push(`残高がマイナスになっています。`)
      }

      // 不整合なデータを見つける
      if (this.bonusList.length) {
        let group = null
        let rank = null
        for (let i = 0; i < this.bonusList.length; i++) {
          const e = this.bonusList[i]
          // 対象外以外
          if (e.na) {
            break
          }
          // 順位が下なのに、評価が上になっているデータを見つける
          // グループが変わればリセット、1番目は比較しない
          if (group != e.groupNum) {
            rank = e.rankName
            group = e.groupNum
          // 1行上の人とランクが違う場合
          } else if (rank != e.rankName) {
            let rankArr = utils.clone(RANK_LIST)
            const idx = rankArr.indexOf(rank)
            rankArr = rankArr.slice(idx + 1)
            if (!rankArr.includes(e.rankName)) {
              e.err = true
              this.errData.push(`${e.groupNum}グループの ${e.userName} さんの 評価 が高いです。加点するか、評価を下げてください。`)
            }
            rank = e.rankName
          }

          // 始末書枚数
          if (e.apology) {
            const val = Number(e.apology)
            let name = '始末書'
            // マイナスになっている
            if (val < 0) {
              e.err = true
              this.errData.push(`${e.groupNum}グループの ${e.userName} さんの ${name} がマイナスです。`)
            }
          }

          // 反省文枚数
          if (e.reflect) {
            const val = Number(e.reflect)
            let name = '反省文'
            // マイナスになっている
            if (val < 0) {
              e.err = true
              this.errData.push(`${e.groupNum}グループの ${e.userName} さんの ${name} がマイナスです。`)
            }
          }
        }
      }

      this.filterBtn()

      this.loading = false
    },

    /**
     * 選択されたグループを表示
     */
    setGroupList() {
      if (!this.group) {
        this.list = utils.clone(this.bonusList)

      // 2G/1G表示
      } else if (this.group == 2) {
        const list = this.bonusList.filter((b) => {
          return [1, 2].includes(b.groupNum)
        })
        this.list = list

      // その他のグループ
      } else {
        const list = this.bonusList.filter((b) => {
          return b.groupNum == this.group
        })
        this.list = list
      }
    },

    /**
     * 検索ボタン
     */
    filterBtn() {
      this.setGroupList()
      if (this.filter.boss) {
        this.filterData()
      }
      this.filterData2()
    },

    /**
     * 検索
     */
    filterData() {
      // eslint-disable-next-line no-irregular-whitespace
      const boss = this.filter.boss.replace(/ /g, '').replace(/　/g, '')
      const list = this.list.filter((l) => {
        // eslint-disable-next-line no-irregular-whitespace
        return !l.na && l.boss && l.boss.replace(/ /g, '').replace(/　/g, '').indexOf(boss) > -1
      })
      this.list = list
    },

    /**
     * 検索
     */
    filterData2() {
      if (this.filter.input) {
        let list = []
        // 未入力あり
        if (this.filter.input == 1) {
          list = this.list.filter((l) => {
            return !l.na && (l.smoking == null || l.drivePrice == null)
          })
        // 週休3日対象者
        } else if (this.filter.input == 2) {
          list = this.list.filter((l) => {
            return !l.na && l.weeklyHolidayDenominatorDefault
          })
        // 週休3日係数あり
        } else if (this.filter.input == 3) {
          list = this.list.filter((l) => {
            return !l.na && l.weeklyHolidayNumerator
          })
        // 上司面談6回未満
        } else if (this.filter.input == 4) {
          list = this.list.filter((l) => {
            return !l.na && (!l.bossCount || l.bossCount < 6)
          })
        // 時短対象者
        } else if (this.filter.input == 5) {
          list = this.list.filter((l) => {
            return !l.na && (l.fullTime && Number(l.fullTime) < 8)
          })
        // 係数あり
        } else if (this.filter.input == 6) {
          list = this.list.filter((l) => {
            return !l.na && l.denominator
          })
        }
      this.list = list
      }
    },

    /**
     * 切替イベント
     * @paran tab 選択したタブ
     */
    async periodChanged(tab) {
      this.period = tab.id
      await this.getData()
    },

    /**
     * グループ切替イベント
     */
    groupChanged(tab) {
      this.group = tab.id
      if (!this.group) {
        this.showInfo = false
      }
      this.filterBtn()
    },

    /**
     * 総額変更
     */
    async changeBonus() {
      if (!this.filter.year || !this.period) {
        await dialogs.showErrorDialog('年度、期 が未選択', '年度と期 を選択しているか確認してください。')
        this.bonus = this.bonus2 / 10000
        return
      }
      let bonus = 0
      if (this.bonus) {
        bonus = this.bonus * 10000
      }
      await backend.postData('evaluation/saveBonus', { ...this.filter, period: this.period, bonus, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '総額更新', `${this.filter.year}年度 ${this.periodName} の総額を ${this.bonus} 万円にしました。`)
    },

    /**
     * スクロールでの数字変更不可
     */
    inputFocus(e) {
      e.target.addEventListener(
        'wheel',
        (e) => {
          e.preventDefault()
        }, { passive: false }
      )
    },

    /**
     * グループ単価変更
     */
    async changeGroupUnitPrice(row) {
      if (!this.filter.year || !this.period) {
        await dialogs.showErrorDialog('年度、期 が未選択', '年度と期 を選択しているか確認してください。')
        return
      }
      if (!row.uniPrice) {
        row.uniPrice = 0
      }
      await backend.postData('evaluation/saveGroupUnitPrice', { ...this.filter, period: this.period, groupNum: row.groupNum, unitPrice: row.unitPrice, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', 'グループ単価更新', `${this.filter.year}年度 ${this.periodName} の${row.groupNum}G単価を ${row.unitPrice} 円にしました。`)
    },

    /**
     * 調整点変更
     */
    async changeAddPoint(row, e) {
      if (!e.target.value || e.target.value == '0') {
        row.addPoints = null
      }
      await backend.postData('evaluation/saveAddPoints', { id: row.id, addPoints: row.addPoints, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '調整点', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの調整点を ${e.target.value || 0} 点にしました。`)
    },

    /**
     * 評価変更
     * @param row 対象行
     */
    async changeRank(row) {
      this.loading = true
      await backend.postData('evaluation/saveRank', { id: row.id, rankName: row.rankName, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '評価変更', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの評価を ${row.rankName} にしました。`)
    },

    /**
     * 禁煙手当変更
     * @param row 対象行
     */
    async changeSmoking(row) {
      this.loading = true
      await backend.postData('evaluation/saveSmoking', { id: row.id, smoking: row.smoking, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '禁煙手当変更', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの禁煙手当を ${row.smoking} 円にしました。`)
    },

    /**
     * 安全運転手当変更
     * @param row 対象行
     */
    async changeDrivePrice(row) {
      this.loading = true
      await backend.postData('evaluation/saveDrivePrice', { id: row.id, drivePrice: row.drivePrice, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '運転手当変更', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの安全運転手当を ${row.drivePrice} 円にしました。`)
    },

    /**
     * 始末書変更
     */
    async changeApology(row, type, e) {
      let name = '始末書'
      if (type == 'reflect') {
        name = '反省文'
      }
      if (!e.target.value) {
        row[type] = null
      }
      const val = Number(e.target.value)
      // マイナスになっている
      if (val < 0) {
        await dialogs.showErrorDialog('不正な値', 'マイナスのため、保存できません。')
        row.err = true
        this.errData.push(`${row.groupNum}グループの ${row.userName} さんの ${name} がマイナスです。`)
        return
      }
      // 少数になっている
      if (!Number.isInteger(val)) {
        await dialogs.showErrorDialog('不正な値', '少数のため、保存できません。')
        row.err = true
        this.errData.push(`${row.groupNum}グループの ${row.userName} さんの ${name} に少数が入っています。`)
        return
      }
      await backend.postData('evaluation/saveApology', { id: row.id, apology: row.apology, reflect: row.reflect, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', name + '変更', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの${name}を ${e.target.value || 0} 枚にしました。`)
    },

    /**
     * 係数保存
     */
    async changeCoef(name, pair, row, e) {
      let don = 'denominator'
      let num = 'numerator'
      if (name == '週休3日') {
        don = 'weeklyHolidayDenominator'
        num = 'weeklyHolidayNumerator'
      }
      let res = 'なし'
      // 数字を削除、もしくは0が入力されたらもう片方もNULL
      if (!e.target.value || e.target.value == '0') {
        row[don] = null
        row[num] = null
      // 数字が入力された場合
      } else {
        // もう片方が未入力なら何もしない
        if (!pair) {
          return
        }
        const val = Number(e.target.value)
        // マイナスになっている
        if (val < 0) {
          await dialogs.showErrorDialog('不正な値', 'マイナスのため、保存できません。')
          row.err = true
          this.errData.push(`${row.groupNum}グループの ${row.userName} さんの ${name} がマイナスです。`)
          return
        }
        // 少数になっている
        if (!Number.isInteger(val)) {
          await dialogs.showErrorDialog('不正な値', '少数のため、保存できません。')
          row.err = true
          this.errData.push(`${row.groupNum}グループの ${row.userName} さんの ${name} に少数が入っています。`)
          return
        }
        // 分子の方が大きい
        if (Number(row[don]) < Number(row[num])) {
          await dialogs.showErrorDialog('不正な値', '分子の方が大きいため、保存できません。')
          row.err = true
          this.errData.push(`${row.groupNum}グループの ${row.userName} さんの ${name} が分子のほうが大きくなっています。`)
          return
        }
        res = row[don] + '分の' + row[num]
      }
      const param = { id: row.id, user: this.$store.getters.user.id }
      param.don = don
      param.num = num
      param[don] = row[don]
      param[num] = row[num]
      await backend.postData('evaluation/saveCoef', param)
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', name + '変更', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんの${name}を ${res} にしました。`)
    },

    /**
     * 対象外変更
     */
    async changeNA(row, type) {
      let msg = ''
      // 評価対象外
      if (type == 'na') {
        // チェック
        if (row.na) {
          // 賞与対象外をはずす
          row.naBonus = false
          msg = '評価対象外に'
        // 解除
        } else {
          msg = '評価対象外解除'
        }

      // 賞与対象外
      } else {
        // チェック
        if (row.naBonus) {
          // 賞与対象外をはずす
          row.na = false
          msg = '賞与対象外に'
        // 解除
        } else {
          msg = '賞与対象外解除'
        }
      }

      await backend.postData('evaluation/saveBonusNA', { id: row.id, na: row.na, naBonus: row.naBonus, type, user: this.$store.getters.user.id })
      this.getData()
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '対象外', `${this.filter.year}年度 ${this.periodName} の ${row.userName} さんを${msg}しました。`)
    },

    /**
     * 評価が変わるときに線を太くする
     */
    setStyle(row, i) {
      let border = 'border-b border-gray-200'
      if (i != this.list.length - 1 && row.rankName != this.list[i+1].rankName) {
        border = 'border-b-4 border-gray-400'
      }

      // 全グループ表示＆最終行ではない
      if (!this.group && i != this.list.length - 1) {
        // 1Gと2Gの間は緑にしない
        if (!(row.groupNum == 2 && this.list[i+1].groupNum == 1) && !(row.groupNum == 1 && this.list[i+1].groupNum == 2) && (row.groupNum != this.list[i+1].groupNum)) {
          border = 'border-b-8 border-green-500'
        }
      }
      return border
    },

    /**
     * 同位の背景色
     */
    setStyle2(val) {
      if (val > 1) {
        return 'bg-yellow-100'
      }
    },

    /**
     * 評価シート遷移
     * @param row 対象行
     */
    toEvaluationForm(row) {
      const target = {
        year: this.filter.year,
        period: this.period,
        userName: row.userName,
        userId: row.userId,
      }
      const tar = JSON.stringify(target)
      sessionStorage.setItem('FROM_BONUS_TO_EVALUATION', tar)
      let route = this.$router.resolve({
          name: 'EvaluationInput',
          query: {
            id: row.id,
            mode: 'view'
          }
      });
      window.open(route.href, '_blank')
    },

    /**
     * 賞与確定
     */
    async bonusDec() {
      if (!this.admin) {
        return
      }
      if (this.errData && this.errData.length) {
        await dialogs.showErrorDialog('不整合データありのため確定不可', this.errData.join('\r\n'))
        return
      }
      for (let i = 0; i < this.unitPriceList.length; i++) {
        const e = this.unitPriceList[i]
        if (!e.unitPrice || isNaN(e.unitPrice) || Number(e.unitPrice) <= 0) {
          for (let j = 0; j < this.pointList.length; j++) {
            const p = this.pointList[j]
            if (Number(p.groupNum) == Number(e.groupNum)) {
              if (p.numSS || p.numS || p.numA || p.numB || p.numC || p.numD || p.numE) {
                await dialogs.showErrorDialog('グループ' + p.groupNum + ' の単価が不正', 'グループ' + p.groupNum + ' の単価をご確認ください。')
                return
              }
            }
          }
        }
      }
      let msg = []
      for (let i = 0; i < this.bonusList.length; i++) {
        const e = this.bonusList[i]
        // 対象外はスルー
        if (e.na) {
          continue
        }
        if (e.smoking == null) {
          msg.push(`${e.groupNum}グループの ${e.userName} さんの 禁煙手当 が未入力です。\r\n 0円になりますがよろしいですか？`)
        }
        if (e.drivePrice == null) {
          msg.push(`${e.groupNum}グループの ${e.userName} さんの 安全運転手当 が未入力です。\r\n 0円になりますがよろしいですか？`)
        }
        if (e.apology == null) {
          msg.push(`${e.groupNum}グループの ${e.userName} さんの 始末書 が未入力です。\r\n よろしいですか？`)
        }
        if (e.reflect == null) {
          msg.push(`${e.groupNum}グループの ${e.userName} さんの 反省文 が未入力です。\r\n よろしいですか？`)
        }
      }
      let log = null
      if (msg.length) {
        log = await dialogs.showConfirmDialog('確認事項があります。確定してよろしいですか？', msg.join('\r\n'))
      } else {
        log = await dialogs.showConfirmDialog('確定してよろしいですか？', '今期の賞与を確定します。賞与面談開始可能になります。')
      }
      if (log != 'YES') {
        return
      }
      await backend.postData('evaluationBonusResult/saveResult', { list: this.bonusList, year: this.filter.year, period: this.period, benefit: this.benefit, totalPayment: this.totalPayment, userId: this.$store.getters.user.id })
      await dialogs.showSuccessDialog('確定完了', 'データを確定しました。' + this.filter.year + '年度 ' + this.periodName + ' の賞与面談を行うことができます。')
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '賞与確定', `${this.filter.year}年度 ${this.periodName} の賞与が確定しました。`)
      this.getData()
    },

    /**
     * 確定解除
     */
    async bonusCancel() {
      await dialogs.showInfoDialog('編集可能になりますので、面談は一時中断します', '解除中は賞与面談できません。\r\n編集終了後は再度、賞与確定ボタンを押してください。')
      await backend.postData('evaluationBonusResult/cancel', { year: this.filter.year, period: this.period })
      logManager.recording(this, logManager.Loglevel.INFO, '賞与', '賞与確定解除', `${this.filter.year}年度 ${this.periodName} の確定解除しました。`)
      this.getData()
    },

    /**
     * 始末書集計open
     * @param row
     */
    openApology(row) {
      this.apologyModalData = {
        userId: row.userId,
        userName: row.userName,
        bonusYear: this.filter.year,
        bonusPeriod: this.period
      }
      this.evaluationApologyModalShow = true
    },

    /**
     * 始末書集計close
     */
    closeApology() {
      this.evaluationApologyModalShow = false
      this.apologyModalData = {}
    },

    /**
     * ダウンロードイベント
     */
    async download() {
      const log = await dialogs.showConfirmDialog3('選択してください', '『評価対象外』の人は出力されません。\n※『賞与支給無』は出力します。', 'キャンセル', '一覧Excel', '給与奉行CSV')

      // キャンセル
      if (log == '1') return

      this.loading = true
      let period = '上期'
      if (this.period == 2) {
        period = '下期'
      }
      const name = '評価シート（' + this.filter.year + '年度' + period + '）'
      let a
      let url
      try {
        // CSV
        if (log == '3') {
          const res = await backend.postBlob2('evaluationDownload/csv', { year: this.filter.year, period: this.period })
          const uint8Array = res.data
          const blob = new Blob([uint8Array], { type: 'text/csv;charset=utf-8;' })
          a = document.createElement("a")

          url = URL.createObjectURL(blob)
          a.href = url

          a.download = name + '.csv'

        // Excel
        } else {
          const res = await backend.postBlob2('evaluationDownload/excel', { list: this.bonusList, year: this.filter.year, period })
          const uint8Array = res.data
          const blob = new Blob([uint8Array], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          a = document.createElement("a")

          url = URL.createObjectURL(blob)
          a.href = url

          a.download = name + '.xlsx'
        }

        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        this.loading = false
      } catch (e) {
        alert(e)
        this.loading = false
      }
    },

    /**
     * スタンプ表モーダルopen
     */
    stampTable() {
      this.openStampTableShow = true
    }
  }
}
</script>

<style scoped>
.main-form {
  min-width: 1100px;
}

.main-height {
  max-height: calc(100% - 10px);
}

.table-height {
  max-height: calc(100% - 311px);
}

.table-height2 {
  max-height: calc(100% - 36px);
}

.table {
  width: 100%;
}

.bg-colu {
  background: #ebf0fb;
}

.hover-color {
  background: white;
}

.hover-color:hover {
  background: rgb(239, 250, 250);
}
</style>